import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { de as deLocale } from 'date-fns/locale';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { format } from 'date-fns';
import { MitarbeiterList } from '../../types';
import { PendingZeitraum } from './auditDetailTypes';

interface PendingZeitraeumeEditorProps {
  pendingZeitraeume: PendingZeitraum[];
  mitarbeiter: MitarbeiterList[];
  onAdd: () => void;
  onUpdate: (tempId: number, field: keyof PendingZeitraum, value: string | number[]) => void;
  onRemove: (tempId: number) => void;
  getDateError: (zeitraum: { startdatum: string; enddatum: string }) => string;
}

const PendingZeitraeumeEditor: React.FC<PendingZeitraeumeEditorProps> = ({
  pendingZeitraeume,
  mitarbeiter,
  onAdd,
  onUpdate,
  onRemove,
  getDateError,
}) => (
  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={deLocale}>
    <Box>
      {pendingZeitraeume.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
          {pendingZeitraeume.map((zeitraum, idx) => {
            const dateError = getDateError(zeitraum);

            return (
              <Paper
                key={zeitraum.tempId}
                variant="outlined"
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  borderColor: dateError ? 'error.main' : 'divider',
                  position: 'relative',
                }}
              >
                <Box
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    Phase {idx + 1}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => onRemove(zeitraum.tempId)}
                    sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <DatePicker
                      label="Startdatum"
                      value={zeitraum.startdatum ? new Date(zeitraum.startdatum) : null}
                      onChange={(date) =>
                        onUpdate(
                          zeitraum.tempId,
                          'startdatum',
                          date ? format(date, 'yyyy-MM-dd') : '',
                        )
                      }
                      format="dd.MM.yyyy"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          placeholder: 'Start wählen',
                        },
                        openPickerButton: { sx: { mr: -0.5 } },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <DatePicker
                      label="Enddatum"
                      value={zeitraum.enddatum ? new Date(zeitraum.enddatum) : null}
                      onChange={(date) =>
                        onUpdate(
                          zeitraum.tempId,
                          'enddatum',
                          date ? format(date, 'yyyy-MM-dd') : '',
                        )
                      }
                      format="dd.MM.yyyy"
                      minDate={zeitraum.startdatum ? new Date(zeitraum.startdatum) : undefined}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          placeholder: 'Ende wählen',
                          error: !!dateError,
                          helperText: dateError,
                        },
                        openPickerButton: { sx: { mr: -0.5 } },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Beschreibung"
                      value={zeitraum.beschreibung}
                      onChange={(event) =>
                        onUpdate(zeitraum.tempId, 'beschreibung', event.target.value)
                      }
                      placeholder="z.B. Recon-Phase"
                      sx={{ '& .MuiInputBase-root': { height: 56 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>Team</InputLabel>
                      <Select
                        multiple
                        value={zeitraum.mitarbeiter_ids}
                        onChange={(event) =>
                          onUpdate(
                            zeitraum.tempId,
                            'mitarbeiter_ids',
                            event.target.value as number[],
                          )
                        }
                        input={<OutlinedInput label="Team" />}
                        renderValue={(selected) =>
                          (selected as number[])
                            .map((id) => {
                              const person = mitarbeiter.find((entry) => entry.id === id);
                              return person ? `${person.vorname} ${person.nachname[0]}.` : '';
                            })
                            .filter(Boolean)
                            .join(', ')
                        }
                      >
                        {mitarbeiter.map((person) => (
                          <MenuItem key={person.id} value={person.id}>
                            <Checkbox checked={zeitraum.mitarbeiter_ids.indexOf(person.id) > -1} />
                            <ListItemText primary={`${person.vorname} ${person.nachname}`} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Paper>
            );
          })}
        </Box>
      )}

      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={onAdd}
        size="small"
        sx={{ borderStyle: 'dashed' }}
      >
        Zeitraum hinzufügen
      </Button>

      {pendingZeitraeume.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mb: 0.5 }}>
            Vorschau
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {pendingZeitraeume
              .filter((zeitraum) => zeitraum.startdatum && zeitraum.enddatum)
              .map((zeitraum, idx) => {
                const start = zeitraum.startdatum
                  ? format(new Date(zeitraum.startdatum), 'dd.MM.yyyy')
                  : '';
                const end = zeitraum.enddatum
                  ? format(new Date(zeitraum.enddatum), 'dd.MM.yyyy')
                  : '';

                return (
                  <Chip
                    key={zeitraum.tempId}
                    label={`Phase ${idx + 1}: ${start} – ${end}`}
                    size="small"
                    variant="outlined"
                    color={getDateError(zeitraum) ? 'error' : 'default'}
                  />
                );
              })}
          </Box>
        </Box>
      )}
    </Box>
  </LocalizationProvider>
);

export default PendingZeitraeumeEditor;
